/**
 * Entity Icons Constants
 *
 * Centralized icon definitions for all entity types in the application.
 * This ensures consistency and makes it easy to update icons across the entire app.
 *
 * @example
 * ```tsx
 * import { ENTITY_ICONS } from '@/constants/entityIcons';
 *
 * <ContentHeader
 *   icon={ENTITY_ICONS.department}
 *   title="Departments"
 * />
 * ```
 */

import { Building2, IdCard, Package, UsersRound, type LucideIcon } from 'lucide-react';
import type { IconType } from 'react-icons';
import type { OrgFunctionKind } from '@bliks-io/contracts';
import { ENTITY_KIND_ICONS, SUCCESSOR_ICON } from '@bliks-io/ui';

/**
 * Union type for all icon types used in the application.
 * Supports both Lucide React icons and React Icons.
 */
export type AppIcon = LucideIcon | IconType;

/**
 * Entity icon constants for all business entities and organizational units.
 *
 * Usage:
 * - Import this constant wherever you need entity icons
 * - Use the appropriate key for the entity type
 * - Icons are already imported and ready to use
 */
export const ENTITY_ICONS = {
  // Business Entities
  /** Icon for departments */
  department: ENTITY_KIND_ICONS.department,

  /** Icon for teams */
  team: ENTITY_KIND_ICONS.team,

  /** Icon for roles */
  role: ENTITY_KIND_ICONS.role,

  /** Icon for OrgFunctions (the umbrella over teams + roles; the single
   *  source of truth for every "Funktionen" surface — sidebar nav, the
   *  catalog list/form headers, and the suggestion review/picker flow keyed
   *  on the `orgFunction` suggestion kind). Distinct from team's group glyph
   *  and role's briefcase so the umbrella never reads as one of its members. */
  orgFunction: IdCard,

  /** Icon for individual users */
  user: ENTITY_KIND_ICONS.person,

  /** Icon for organization members */
  orgMember: UsersRound,

  /** Icon for external contacts */
  externalContact: ENTITY_KIND_ICONS.externalContact,

  /** Icon for software systems */
  system: ENTITY_KIND_ICONS.system,

  /** Icon for the successor / Bearbeiter concept (who takes over the next
   *  step, or does the current one in assignee mode). Single source for the
   *  SuccessorCard header and the successor picker dialog header. */
  successor: SUCCESSOR_ICON,

  /** Icon for artifacts/documents */
  artifact: ENTITY_KIND_ICONS.artifact,

  /** Icon for processes */
  process: ENTITY_KIND_ICONS.process,

  // Organizational
  /** Icon for organization/company */
  organization: Building2,

  /** Icon for packages */
  package: Package,

  /** Icon for groups of members/users */
  members: UsersRound
} as const;

/**
 * Type-safe entity type keys.
 * Use this type when you need to reference entity types in a type-safe way.
 */
export type EntityType = keyof typeof ENTITY_ICONS;

/**
 * The per-kind entity icon COMPONENT for an OrgFunction: teams get the group
 * glyph, roles (Stellen) the briefcase. The single home of the kind→icon
 * mapping — every surface that needs its own sizing renders this component
 * rather than re-deriving the ternary.
 */
export function orgFunctionKindIconComponent(kind: OrgFunctionKind): AppIcon {
  return kind === 'team' ? ENTITY_ICONS.team : ENTITY_ICONS.role;
}

/**
 * The per-kind entity icon for an OrgFunction, ready to render at the shared
 * picker sizing (dropdown rows, triggers, tags).
 */
export function orgFunctionKindIcon(kind: OrgFunctionKind): React.ReactNode {
  const Icon = orgFunctionKindIconComponent(kind);
  return <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />;
}
